import {
  Box,
  SelectChangeEvent,
  Select,
  MenuItem,
  Input,
  Chip,
  useTheme,
} from "@mui/material";
import { memo, useMemo, useCallback, useState } from "react";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { PerspectiveViewFilter } from "../../entities/perspective_view_filter";
import { formatColumnCaption } from "../../utils/mui_table_utils";
import { MultiSelect } from "../multi_select";
import { MinimalButton } from "./minimal_button_component";

const MemoizedCheckIcon = memo(() => (
  <CheckIcon fontSize="small" sx={{ color: "#7ca915", width: "15px" }} />
));

export const FilterComponent = ({
  viewFilters,
  viewFilter,
  filterIndex,
  handleChange,
  handleSave,
  handleDelete,
  columns,
}: {
  viewFilters: PerspectiveViewFilter[];
  viewFilter: PerspectiveViewFilter;
  filterIndex: number;
  handleChange: (
    f: PerspectiveViewFilter,
    filterIndex: number,
    immediateSave?: boolean
  ) => void;
  handleSave: () => void;
  handleDelete: (filter: [string, string, string[] | string]) => void;
  columns: {
    name: string;
    caption: string;
    unique_values: string[];
  }[];
}) => {
  const theme = useTheme();
  const isSaved = useMemo(() => {
    return viewFilters.some(
      (filter) =>
        filter[0] === viewFilter[0] &&
        filter[1] === viewFilter[1] &&
        JSON.stringify(filter[2]) === JSON.stringify(viewFilter[2])
    );
  }, [viewFilters, viewFilter]);

  const stableHandleSave = useCallback(() => {
    if (document.activeElement && viewFilter[1] === "contains") {
      // this one will do an automatic save
      (document.activeElement as HTMLElement).blur();
    } else {
      handleSave();
    }
  }, [handleSave, viewFilter]);

  const buttonSx = useMemo(() => ({ mr: 0.5 }), []);

  const [inputValue, setInputValue] = useState(
    Array.isArray(viewFilter[2]) ? viewFilter[2][0] : viewFilter[2]
  );

  const sortedOptions = useMemo(() => {
    if (!columns.find((column) => column.name === viewFilter[0])) return [];

    return [
      ...columns.find((column) => column.name === viewFilter[0])!.unique_values,
    ]
      .filter((value) => value)
      .sort((a: string, b: string) =>
        a.toLowerCase().localeCompare(b.toLowerCase())
      );
  }, [columns, viewFilter]);

  return (
    <Box sx={{ display: "flex" }}>
      <Box sx={{ mt: 0.15 }}>
        <Select
          value={viewFilter[0]}
          renderValue={(value) => (
            <Chip
              key={value}
              label={formatColumnCaption(value)}
              size="small"
              sx={{
                backgroundColor: "action.selected",
              }}
            />
          )}
          onChange={(e: SelectChangeEvent) => {
            handleChange(
              [e.target.value, viewFilter[1], viewFilter[2]],
              filterIndex
            );
          }}
          variant="standard"
          size="small"
          displayEmpty
          sx={{ mr: 1 }}
        >
          {columns
            .slice()
            .sort((a, b) =>
              a.name.toLowerCase().localeCompare(b.name.toLowerCase())
            )
            .map((o) => (
              <MenuItem key={o.name} value={o.name}>
                {formatColumnCaption(o.name)}
              </MenuItem>
            ))}
        </Select>

        <Select
          value={viewFilter[1]}
          renderValue={(value) => (
            <Box key={value} sx={{ fontSize: "13px" }}>
              {formatColumnCaption(value)}
            </Box>
          )}
          onChange={(e: SelectChangeEvent) => {
            handleChange(
              [viewFilter[0], e.target.value, viewFilter[2]],
              filterIndex
            );
          }}
          variant="standard"
          size="small"
          sx={{ mr: 1 }}
        >
          <MenuItem value="in">in</MenuItem>
          <MenuItem value="not in">not in</MenuItem>
          <MenuItem value="contains">contains</MenuItem>
        </Select>
      </Box>

      {viewFilter[1] === "contains" && (
        <Input
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onBlur={(e) => {
            handleChange(
              [viewFilter[0], viewFilter[1], [inputValue]],
              filterIndex,
              true
            );
          }}
          sx={{ mr: 1, mt: -0.25 }}
        />
      )}

      {viewFilter[1] !== "contains" && sortedOptions.length > 0 && (
        <MultiSelect
          searchable
          noFormatting
          options={sortedOptions}
          selectedOptions={
            Array.isArray(viewFilter[2]) ? viewFilter[2] : [viewFilter[2]]
          }
          handleChange={(event: SelectChangeEvent<string[]>) => {
            const selectedValues = Array.isArray(event.target.value)
              ? event.target.value
              : [event.target.value];
            handleChange(
              [viewFilter[0], viewFilter[1], selectedValues],
              filterIndex
            );
          }}
          handleDelete={(option: string) => {
            // @ts-ignore
            const newOptions = viewFilter[2]!.filter(
              (v: string) => v !== option
            );
            handleChange(
              [viewFilter[0], viewFilter[1], newOptions],
              filterIndex
            );
          }}
        />
      )}

      <MinimalButton
        onClick={stableHandleSave}
        sx={buttonSx}
        backgroundColor={
          isSaved ? theme.palette.atomic.green1 : "action.selected"
        }
      >
        <MemoizedCheckIcon />
      </MinimalButton>

      <MinimalButton
        onClick={(event) => {
          handleDelete(viewFilter);
        }}
        sx={{ mr: 2 }}
      >
        <CloseIcon fontSize="small" sx={{ color: "#F75539", width: "15px" }} />
      </MinimalButton>
    </Box>
  );
};
